import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import DialogMUI from '../../../shared/DialogMUI';
import ButtonMUI from '../../../shared/ButtonMUI';
import {closeModal} from '../../../redux/actions/app/appActions';
import SelectComponent from '../../../shared/SelectComponent';
import {Controller, useFieldArray, useForm} from 'react-hook-form';
import * as yup from 'yup';
import {yupResolver} from '@hookform/resolvers/yup';
import './ProjectsModal.scss';
import {ReactComponent as PlusIcon} from '../../../assets/icons/plus-icon.svg';
import {ReactComponent as RemoveIcon} from '../../../assets/icons/remove.svg';
import {AutocompleteMUI} from '../../../shared';
import {POPUPS} from '../../../redux/actions/app/appActionTypes';
import LoadPanel from '../../Components/LoadPanel';
import {notifyError, notifySuccess} from '../../../helpers/notifySnack';
import {getCities, getCountries} from '../../../redux/actions/countries/countriesActions';
import DropzoneAvatar from '../../Components/DropzoneAvatar';
import {
    createProject,
    getProjects,
    getProjectsAvatars,
    getProjectTypes
} from '../../../redux/actions/projects/projectsActions';
import InputMUI from '../../../shared/InputMUI';
import {getCompaniesClientsPopup} from '../../../redux/actions/clients/clientsActions';
import {getCompanyEmployeeListPopup} from '../../../redux/actions/employee/employeeActions';
import {useTranslation} from 'react-i18next';
import {getCompanies} from '../../../redux/actions/company/companyActions';
import {useHistory} from 'react-router-dom';
import {mainPath} from '../../../routes/paths';

const ProjectsModal = () => {
    const {countriesLoading, countries, cities, citiesLoading} = useSelector(({countries}) => countries);
    const companyList = useSelector(({company}) => company.companies);
    const {companiesClientsPopup, companiesClientsPopupLoading} = useSelector(({clients}) => clients);
    const {projectTypes, projectTypesLoading,avatars} = useSelector(({projects}) => projects);
    const [filteredCities, setFilteredCities] = useState([]);
    const {companyEmployeesListPopup, companyEmployeesListPopupLoading} = useSelector(({employee}) => employee);
    const dispatch = useDispatch();
    const popupToShow = useSelector(({app}) => app.popupToShow);
    const {t} = useTranslation();
    const history = useHistory();
    const onCloseHandler = () => {
        dispatch(closeModal());
    };

    const onSubmit = async (data) => {
        const formData = new FormData();

        formData.append('cityId', data.city);
        formData.append('companyId', data.companyId);
        formData.append('country', data.country);
        formData.append('address', data.address);

        data.employee.forEach((employee, index) => {
            formData.append(`employees[${index}]`, employee.id);
        });

        data.clients.forEach((client, index) => {
            formData.append(`clients[${index}]`, client.id);
        });

        if (data.avatar) {
            formData.append('logoId', data.avatar);
        }

        data.projects?.forEach((item, index) => {
            formData.append(`typeIds[${index}]`, item.project);
        });

        const res = await dispatch(createProject(formData));

        if (res.payload && res.payload.status === 201) {
            await dispatch(getProjects());
            await dispatch(getCompanies());
            notifySuccess(t('all.successfully'));
            onCloseHandler();
            history.push(mainPath.projects);
        }
    };

    const schema = yup.object().shape({
        companyId: yup.string().required(t('ProjectsEditing.company_required')),
        city: yup.string().required(t('ProjectsEditing.city_required')),
        country: yup.string().required(t('ProjectsEditing.country_required')),
        address: yup.string().required(t('ProjectsEditing.address_required')),
        projects: yup
            .array()
            .of(
                yup.object().shape({
                    project: yup.string().required(t('ProjectsEditing.project_required'))
                })
            )
            .required(t('ProjectsEditing.project_required'))
    });

    const {
        control,
        handleSubmit,
        watch,
        reset,
        setValue,
        formState: {errors, isSubmitting, isValid}
    } = useForm({
        mode: 'all',
        reValidateMode: 'onChange',
        shouldFocusError: true,
        resolver: yupResolver(schema),
        defaultValues: {
            companyId: '',
            address: '',
            city: '',
            country: '',
            projects: [{project: ''}],
            employee: [],
            clients: [],
            avatar: null
        }
    });

    const {fields, append, remove} = useFieldArray({
        control,
        name: 'projects'
    });

    const onFileAdded = (avatar) => {
        setValue('avatar', avatar);
    };

    const popupVisible = popupToShow === POPUPS.PROJECT_POPUP;

    const handleCountryChange = (selectedCountryId) => {
        const filtered = cities.filter((city) => city.countryId === selectedCountryId);
        setFilteredCities(filtered);
    };

    const employeeArr = companyEmployeesListPopup
        .filter(item => item.status === 'active')
        .map(item => ({
            label: item.firstName ?? '',
            id: item.id,
            avatar: item.avatar ?? '',
            text: item.roleName ?? ''
        }));

    const clientsArr = companiesClientsPopup
        .map(item => ({
            label: item.firstName ?? '',
            id: item.id,
            avatar: item.avatar ?? '',
            text: item.roleName ?? '',
            isSpectator: item.role === 'spectator',
        }));

    const projectsWatch = watch('projects');
    const companyIdWatch = watch('companyId');
    const avatarWatch = watch('avatar');

    const getFilteredOptions = (index) => {
        const selectedIds = projectsWatch.map((field) => field.project).filter((id) => id !== undefined);
        return projectTypes.filter((option) => !selectedIds.includes(option.id) || selectedIds[index] === option.id);
    };

    useEffect(() => {
        const countryId = watch('country');
        setValue('city', '');
        handleCountryChange(countryId);
    }, [cities, watch('country')]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                if (companyIdWatch.length) {
                    setValue('employee', []);
                    setValue('clients', []);
                    await dispatch(getCompaniesClientsPopup(companyIdWatch));
                    await dispatch(getCompanyEmployeeListPopup(companyIdWatch));
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };

        fetchData();
    }, [companyIdWatch]);

    useEffect(() => {
        const fetchFunc = async () => {
            try {
                if (projectTypesLoading) {
                    const projectTypesRes = await dispatch(getProjectTypes());
                    if (projectTypesRes.error) {
                        onCloseHandler();
                        return;
                    }
                }

                if(avatars?.loading){
                    const projectAvatars = await dispatch(getProjectsAvatars());
                    if (projectAvatars.error) {
                        onCloseHandler();
                        return;
                    }
                }

                if (countriesLoading) {
                    const countriesRes = await dispatch(getCountries());
                    if (countriesRes.error) {
                        onCloseHandler();
                        return;
                    }
                }

                if (citiesLoading) {
                    const citiesRes = await dispatch(getCities());
                    if (citiesRes.error) {
                        onCloseHandler();
                    }
                }
            } catch (error) {
                notifyError(error.message || 'error');
            }
        };

        if (popupVisible) {
            fetchFunc();
        } else {
            reset();
        }
    }, [popupVisible, dispatch]);

    return (
        <div className='project__modal'>
            <DialogMUI open={popupVisible} onClose={onCloseHandler} title={t('ProjectsModal.dialogTitle')}>
                {countriesLoading || citiesLoading || projectTypesLoading || avatars?.loading ? (
                    <LoadPanel staticPos={true} noBg={true}/>
                ) : (
                    <>
                        <div className='project__dropzone'>
                            <DropzoneAvatar
                                needLoader={false}
                                maxFiles={1}
                                maxSizeMb={50}
                                onFileAdded={onFileAdded}
                                images={avatars.list}
                            />
                        </div>

                        <div className='project__selection'>
                            <Controller
                                name='companyId'
                                control={control}
                                render={({field}) => (
                                    <SelectComponent
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        value={companyList.find((c) => c.id === field.value)}
                                        options={companyList}
                                        error={errors.companyId?.message}
                                        label={t('ProjectsModal.selectCompany')}
                                        placeholder=''
                                        inputProps={field}
                                    />
                                )}
                            />
                        </div>

                        {fields.map((field, index) => (
                            <div key={field.id} className='project__input'>
                                <Controller
                                    name={`projects[${index}].project`}
                                    control={control}
                                    render={({field}) => (
                                        <div className={'project__input-inner'}>
                                            <SelectComponent
                                                onChange={field.onChange}
                                                onBlur={field.onBlur}
                                                value={projectTypes.find((c) => c.id === field.value)}
                                                options={getFilteredOptions(index)}
                                                label={`${
                                                    index > 0
                                                        ? t('all.additional') + ' ' + t('ProjectsModal.typeProjectL')
                                                        : t('ProjectsModal.typeProject')
                                                }`}
                                                placeholder=''
                                                error={errors.projects?.[index]?.project?.message}
                                                className={`${index > 0 ? 'with-clear' : ''}`}
                                                inputProps={field}
                                            />
                                            {index > 0 && (
                                                <div className='project__trash' onClick={() => remove(index)}>
                                                    {<RemoveIcon/>}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                />
                            </div>
                        ))}

                        <div className='project__dynamic' onClick={() => append({project: ''})}>
                            <PlusIcon/>
                            <span>{t('ProjectsModal.addProjectType')}</span>
                        </div>

                        <div className='project__select'>
                            <Controller
                                name='country'
                                control={control}
                                render={({field}) => (
                                    <SelectComponent
                                        onChange={(e) => {
                                            field.onChange(e.target?.value ?? '');
                                            setValue('city', '', {shouldValidate: true});
                                        }}
                                        onBlur={field.onBlur}
                                        error={errors.country?.message}
                                        value={countries.find((c) => c.id === field.value)}
                                        options={countries}
                                        label={t('ProjectsModal.country')}
                                        placeholder=''
                                        inputProps={field}
                                    />
                                )}
                            />
                        </div>

                        <div className='project__input'>
                            <Controller
                                name='city'
                                control={control}
                                render={({field}) => (
                                    <SelectComponent
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        value={filteredCities.find((c) => c.id === field.value)}
                                        options={filteredCities}
                                        label={t('ProjectsModal.city')}
                                        error={errors.city?.message}
                                        placeholder=''
                                        inputProps={field}
                                        disabled={!filteredCities.length}
                                    />
                                )}
                            />
                        </div>

                        <div className='project__input'>
                            <Controller
                                name='address'
                                control={control}
                                render={({field}) => (
                                    <InputMUI
                                        type='text'
                                        fullWidth
                                        className='no-icons'
                                        error={errors.address?.message}
                                        inputProps={field}
                                        label={t('ProjectsModal.address')}
                                    />
                                )}
                            />
                        </div>

                        <div className='project__select'>
                            <p>{t('ProjectsModal.addEmployee')}</p>
                            <Controller
                                name='employee'
                                control={control}
                                render={({field}) => {
                                    return (
                                        <AutocompleteMUI
                                            className='no-icons'
                                            text={t('ProjectsModal.selectFromList')}
                                            array={employeeArr}
                                            disabled={!companyIdWatch.length || companyEmployeesListPopupLoading}
                                            value={field.value}
                                            error={errors?.employee}
                                            onChange={field.onChange}
                                        />
                                    );
                                }}
                            />
                        </div>

                        <div className='project__select'>
                            <p>{t('ProjectsModal.addClient')}</p>
                            <Controller
                                name='clients'
                                control={control}
                                render={({field}) => (
                                    <AutocompleteMUI
                                        className='no-icons'
                                        text={t('ProjectsModal.selectFromList')}
                                        array={clientsArr}
                                        disabled={!companyIdWatch.length || companiesClientsPopupLoading}
                                        error={errors?.clients}
                                        value={field.value}
                                        onChange={field.onChange}
                                    />
                                )}
                            />
                        </div>

                        <div className='project__submit'>
                            <ButtonMUI
                                disabled={!isValid || !avatarWatch || isSubmitting}
                                loading={isSubmitting}
                                onClick={handleSubmit(onSubmit)}
                                fullWidth={true}
                            >
                                {t('ProjectsModal.createProject')}
                            </ButtonMUI>
                        </div>
                    </>
                )}
            </DialogMUI>
        </div>
    );
};

export default ProjectsModal;
